import React, { FC } from 'react';

import { Button, Divider } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useTranslation, Trans } from 'react-i18next';

import { useStores } from '@/components/hooks';
import { tryCopyToClipboard } from '@/components/utils';
import { getAWSAmplifyRegion } from '@/api';
import { PUBLIC_ASSETS, RECIPIENT_EMAIL_FOR_LOGIN } from '@/consts';
import appConfig from '@/config/env';
import styles from './NetworkRestrictionExplain.module.scss';

const nameSpace = 'recipientPage.errors';

const getRequiredEndpoints = (): string[] => {
    try {
        const region = getAWSAmplifyRegion(appConfig.cognito.IDENTITY_POOL_ID);
        const apiHostname = new URL(appConfig.API_URL).hostname;
        const baseDomain = apiHostname.split('.').slice(-2).join('.');

        return [
            `*.${baseDomain}`,
            `s3.${region}.amazonaws.com`,
            `cognito-idp.${region}.amazonaws.com`,
            `cognito-identity.${region}.amazonaws.com`,
            `*.auth.${region}.amazoncognito.com`,
        ];
    } catch {
        return [];
    }
};

const formatTimestamp = (): string => {
    const now = new Date();
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const NetworkRestrictionExplain: FC = () => {
    const { t } = useTranslation();
    const { recipientPageStore } = useStores();
    const sharedFilesStore = recipientPageStore?.sharedFilesStore;

    const endpoints = getRequiredEndpoints();

    const sharingUrl = window.location.href;
    const userEmail = sharedFilesStore?.recipientEmail || sessionStorage.getItem(RECIPIENT_EMAIL_FOR_LOGIN) || '';
    const timestamp = formatTimestamp();

    const diagnosticRows = [
        { label: t(`${nameSpace}.networkRestriction_issue`), value: t(`${nameSpace}.networkRestriction_issueValue`) },
        { label: t(`${nameSpace}.networkRestriction_error`), value: t(`${nameSpace}.networkRestriction_errorValue`) },
        { label: t(`${nameSpace}.networkRestriction_url`), value: sharingUrl },
        { label: t(`${nameSpace}.networkRestriction_userEmail`), value: userEmail },
        { label: t(`${nameSpace}.networkRestriction_timestamp`), value: timestamp },
    ];

    const copyEndpoints = (): void => {
        tryCopyToClipboard(endpoints.join('\n'), t(`${nameSpace}.networkRestriction_copyEndpoints`));
    };

    const copyFullMessage = (): void => {
        const lines = [
            t(`${nameSpace}.networkRestriction_itTeamsTitle`),
            '',
            t(`${nameSpace}.networkRestriction_accessDetails`),
            t(`${nameSpace}.networkRestriction_endpointsDescription`),
            ...endpoints,
            '',
            t(`${nameSpace}.networkRestriction_diagnosticTitle`),
            ...diagnosticRows.map(({ label, value }) => `${label}: ${value}`),
        ];
        tryCopyToClipboard(lines.join('\n'), 'Message');
    };

    return (
        <div className={styles.container}>
            <p className={styles.subtitle}>
                <Trans
                    t={t}
                    i18nKey={`${nameSpace}.networkRestriction_subtitle`}
                    components={{
                        1: <img src={`${PUBLIC_ASSETS}/icons/Specter-X-Logo-Dark.svg`} alt="Specter X" className={styles.logo} />,
                    }}
                />
            </p>

            <section className={styles.section}>
                <h4 className={styles['section-subtitle']}>{t(`${nameSpace}.networkRestriction_whatYouCanDo`)}</h4>
                <p className={styles.description}>
                    <Trans
                        t={t}
                        i18nKey={`${nameSpace}.networkRestriction_copyMessage`}
                        components={{
                            1: <Button type="link" className={styles['copy-message-link']} onClick={copyFullMessage} />,
                        }}
                    />
                </p>
            </section>

            <Divider />

            <section className={styles.section}>
                <h3 className={styles['section-title']}>{t(`${nameSpace}.networkRestriction_itTeamsTitle`)}</h3>

                <h4 className={styles['section-subtitle']}>{t(`${nameSpace}.networkRestriction_accessDetails`)}</h4>
                <p className={styles.description}>{t(`${nameSpace}.networkRestriction_endpointsDescription`)}</p>

                <div className={styles['endpoints-box']}>
                    <div className={styles['endpoints-list']}>
                        {endpoints.map((endpoint) => (
                            <div key={endpoint} className={styles.endpoint}>{endpoint}</div>
                        ))}
                    </div>
                    <Button
                        type="link"
                        onClick={copyEndpoints}
                        className={styles['copy-button']}
                    >
                        <CopyOutlined /> {t(`${nameSpace}.networkRestriction_copyEndpoints`)}
                    </Button>
                </div>
            </section>

            <section className={styles.section}>
                <h4 className={styles['section-subtitle']}>{t(`${nameSpace}.networkRestriction_diagnosticTitle`)}</h4>
                <table className={styles['diagnostic-table']}>
                    <tbody>
                        {diagnosticRows.map(({ label, value }) => (
                            <tr key={label}>
                                <td className={styles['diagnostic-label']}>{label}</td>
                                <td className={styles['diagnostic-value']}>{value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className={styles.note}>
                <strong>Note</strong>
                <p>{t(`${nameSpace}.networkRestriction_note`)}</p>
            </section>
        </div>
    );
};

export default NetworkRestrictionExplain;
